/**
 * StudentNavigator — l'entrée des ports pour l'espace élève sur le design system.
 *
 * Monté par la bascule de `common/main-menu/BottomTabsNavigator.js` : un compte qui porte le rôle
 * d'élève, hors live (un compte aussi tuteur compris : les routes poussées, elles, le gardent sur le
 * legacy, `studentSpaceBridge`). `BottomTabsNavigator` garde ce qui n'est pas de l'écran — les
 * relectures du compte et de `static-data`, les notifications, la garde de mise à jour, les effets
 * de coquille. La pastille de « Messages », la barre d'onglets la lit elle-même (`StudentTabBar`).
 *
 * Ce que ce fichier possède : le THÈME de l'espace (Automatique par défaut, `useStudentThemeScope`), les
 * trois onglets — Accueil · Activités · Messages (`TABS_BY_TYPE.student`) — sous la barre du DS
 * (`StudentTabBar`), chacun dans son fond et sous l'en-tête de l'espace (`StudentTabLayout`), et la
 * barre d'état au thème. Les écrans poussés par-dessus (une activité, Sam, le live, le compte…) ne
 * passent pas par ici : ce sont des routes de `navigationScreens.js`, que `studentSpaceBridge`
 * fait basculer une à une.
 */
import React from "react"
import { createBottomTabNavigator, type BottomTabBarProps } from "@react-navigation/bottom-tabs"

import { useStudentThemeScope } from "../../hooks/useStudentThemeScope"
import { ParentStatusBar } from "../parent/ParentStatusBar"
import { StudentActivitiesScreen } from "./Activities/StudentActivitiesScreen"
import { StudentHomeScreen } from "./Home/StudentHomeScreen"
import { STUDENT_TAB_KEY_TO_ROUTE, STUDENT_TAB_TITLES } from "./studentRoutes"
import { StudentTabBar } from "./StudentTabBar"
import { StudentTabLayout } from "./StudentTabLayout"
import { StudentTutoringScreen } from "./Tutoring/StudentTutoringScreen"

const Tab = createBottomTabNavigator()

// Les onglets sont des composants du module, passés en `component` : un rendu de la coquille (chaque
// relecture du compte) ne redessine pas les onglets montés, comme le ferait une fonction en ligne.
function HomeTab() {
    return (
        <StudentTabLayout home>
            <StudentHomeScreen />
        </StudentTabLayout>
    )
}

function ActivitiesTab() {
    return (
        <StudentTabLayout>
            <StudentActivitiesScreen />
        </StudentTabLayout>
    )
}

function TutorTab() {
    return (
        <StudentTabLayout>
            <StudentTutoringScreen />
        </StudentTabLayout>
    )
}

export function StudentNavigator() {
    useStudentThemeScope()

    return (
        <>
            <Tab.Navigator
                // Sans identifiant : les onglets se rejoignent par leur nom, comme ceux du legacy.
                id={undefined}
                tabBar={(tabBarProps: BottomTabBarProps) => <StudentTabBar {...tabBarProps} />}
                screenOptions={{
                    headerShown: false,
                    // Chaque onglet peint son fond (`StudentTabLayout`) : la scène ne doit rien poser dessous.
                    sceneStyle: { backgroundColor: "transparent" },
                }}
            >
                <Tab.Screen
                    name={STUDENT_TAB_KEY_TO_ROUTE.home}
                    component={HomeTab}
                    options={{ headerTitle: STUDENT_TAB_TITLES.home }}
                />
                <Tab.Screen
                    name={STUDENT_TAB_KEY_TO_ROUTE.activities}
                    component={ActivitiesTab}
                    options={{ headerTitle: STUDENT_TAB_TITLES.activities }}
                />
                <Tab.Screen
                    name={STUDENT_TAB_KEY_TO_ROUTE.tutor}
                    component={TutorTab}
                    options={{ headerTitle: STUDENT_TAB_TITLES.tutor }}
                />
            </Tab.Navigator>
            {/* La barre d'état suit le thème (contenu clair sur la nuit) ; montée après la coquille, c'est elle qui gagne. */}
            <ParentStatusBar />
        </>
    )
}
